/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { useEffect } from "react"
import { Link } from "react-router"

import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { AppRoutes } from "../../routes/routes"
import { useViewModel } from "../app/app-hooks"
import { toast } from "../primitives/toast"

// Keep one toast per result type: a new log replaces the existing toast instead
// of stacking another one on screen.
const ERROR_TOAST_ID = "logger-error"
const SUCCESS_TOAST_ID = "logger-success"

/**
 * Connects the logger store to the toast manager. Renders no UI; the `Toaster`
 * that hosts the toasts is owned by the shell in `AppLayout`.
 */
export const LoggerToastEffect = observer(() => {
    const vm = useViewModel(LoggerViewModel)
    const lastError = vm.lastError
    const lastOkLog = vm.lastOkLog

    useEffect(() => {
        if (!lastError) {
            toast.close(ERROR_TOAST_ID)
            return
        }

        toast.add({
            id: ERROR_TOAST_ID,
            type: "error",
            // Announced assertively, through the viewport's `role="alert"` mirror.
            // Success messages stay polite.
            priority: "high",
            // Disable Base UI's dismiss timer because LoggerStore owns the toast lifetime:
            // it clears the log when its timer expires, which closes this toast. A second
            // timer would give the same toast two competing owners.
            timeout: 0,
            title: "Error",
            description: (
                <>
                    {lastError.message || "Error occurred. Please see error log and try again."}
                    <Link
                        to={AppRoutes.Logs}
                        className="mt-1 block text-primary underline underline-offset-4 hover:text-primary/80"
                        onClick={() => toast.close(ERROR_TOAST_ID)}
                    >
                        Show error logs
                    </Link>
                </>
            )
        })
    }, [lastError])

    useEffect(() => {
        if (!lastOkLog) {
            toast.close(SUCCESS_TOAST_ID)
            return
        }

        toast.add({
            id: SUCCESS_TOAST_ID,
            type: "success",
            timeout: 0,
            title: "Success",
            description: lastOkLog.message || "Operation completed successfully."
        })
    }, [lastOkLog])

    return null
})
